import { computed, inject, type Signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { getPermissionState } from '@reducers/auth/permission.helper';
import { AppState } from '@reducers/interfaces';
import type { EnhancedAbsenceModel } from '@reducers/orm/absence/absence.model';
import { AbsenceStatus } from '@reducers/orm/absence/absence.model';
import { checkAbsenteePermission } from '@reducers/orm/absence/absence.permission.helper';
import { enhanceAbsence } from '@reducers/orm/absence/absence.service';
import type { AbsenteeOptionModel } from '@reducers/orm/absentee-option/absentee-option.model';
import type { DepartmentModel } from '@reducers/orm/department/department.model';
import { getDepartmentEntities } from '@reducers/orm/department/department.service';
import type { EmployeeModel } from '@reducers/orm/employee/employee.model';
import type { EmployeeTeamDepartments } from '@reducers/orm/employee/employee.selectors';
import { ExchangeStatusType, type ExchangeWithScheduleModel } from '@reducers/orm/exchange/exchange.model';
import { isExchangeViewable, isIncomingFor, isOutgoingFor } from '@reducers/orm/exchange/exchange.service';
import type { LocationModel } from '@reducers/orm/location/location.model';
import { getLocationEntities } from '@reducers/orm/location/location.service';
import { enhanceOpenShift } from '@reducers/orm/open-shift/open-shift.selector';
import { enhanceSchedule } from '@reducers/orm/schedule/schedule-enhance.helper';
import type { ScheduleModel } from '@reducers/orm/schedule/schedule.model';
import type { ShiftModel } from '@reducers/orm/shift/shift.model';
import { getShiftEntities } from '@reducers/orm/shift/shift.service';
import type { TeamModel } from '@reducers/orm/team/team.model';
import { getTeamEntities } from '@reducers/orm/team/team.service';
import { hasPermission, type PermissionState } from '@sb/shared/permission/util';

import type { PendingAbsenceRow, PendingExchangeRow, PendingOpenShiftRow, PendingRow } from './api/requests.models';

export const isExchangeRow = (row: PendingRow): row is PendingExchangeRow => 'roster_id' in row;

export const isAbsenceRow = (row: PendingRow): row is PendingAbsenceRow => 'startdate' in row;

export const isOpenShiftRow = (row: PendingRow): row is PendingOpenShiftRow => 'occurrence_id' in row;

export interface ExchangeViewContext {
  teams: Record<string, TeamModel>;
  shifts: Record<string, ShiftModel>;
  departments: Record<string, DepartmentModel>;
  locations: Record<string, LocationModel>;
  permissionState: PermissionState;
}

/** Builds an ExchangeViewContext from the store, so both request panels stay in sync when it gains a field. */
export const useExchangeViewContext = (): Signal<ExchangeViewContext> => {
  const store = inject<Store<AppState>>(Store);
  const teams = store.selectSignal(getTeamEntities);
  const shifts = store.selectSignal(getShiftEntities);
  const departments = store.selectSignal(getDepartmentEntities);
  const locations = store.selectSignal(getLocationEntities);
  const permissionState = store.selectSignal(getPermissionState);

  return computed(() => ({
    teams: teams(),
    shifts: shifts(),
    departments: departments(),
    locations: locations(),
    permissionState: permissionState(),
  }));
};

export interface OpenShiftViewContext {
  teams: Record<string, TeamModel>;
  shifts: Record<string, ShiftModel>;
  departments: Record<string, DepartmentModel>;
  locations: Record<string, LocationModel>;
  employees: Record<string, EmployeeModel>;
  authenticatedUserId: string | undefined;
}

export interface AbsenceViewContext {
  employees: Record<string, EmployeeModel>;
  absenteeOptions: Record<string, AbsenteeOptionModel>;
  employeeTeamDepartments: EmployeeTeamDepartments;
  approvableTeamDepartments: EmployeeTeamDepartments;
  permissionState: PermissionState;
}

const hasRoster = (row: PendingExchangeRow): row is PendingExchangeRow & { Roster: ScheduleModel } => !!row.Roster;

export const toVisibleExchanges = (
  rows: PendingExchangeRow[],
  context: ExchangeViewContext,
): ExchangeWithScheduleModel[] => {
  const enhance = enhanceSchedule(context.teams, context.shifts, context.departments, context.locations);

  return rows
    .filter(hasRoster)
    .map((row) => ({ ...row, Schedule: enhance(row.Roster) }))
    .filter((exchange) => isExchangeViewable(exchange, context.permissionState));
};

export const filterSupervisorApprovalExchanges = (
  exchanges: ExchangeWithScheduleModel[],
  permissionState: PermissionState,
): ExchangeWithScheduleModel[] =>
  exchanges.filter((exchange) => {
    if (exchange.status !== ExchangeStatusType.PENDING_SUPERVISOR) {
      return false;
    }

    return hasPermission(
      {
        permissions: ['Approve exchange'],
        userId: 'me',
        departments: exchange.Schedule.Team?.department_id,
      },
      permissionState,
    );
  });

export const filterEmployeeApprovalExchanges = (exchanges: ExchangeWithScheduleModel[]): ExchangeWithScheduleModel[] =>
  exchanges.filter((exchange) => exchange.status === ExchangeStatusType.PENDING_EMPLOYEE);

export const toApprovableAbsences = (rows: PendingAbsenceRow[], context: AbsenceViewContext): EnhancedAbsenceModel[] =>
  rows
    .map((row) =>
      enhanceAbsence(
        row,
        context.employees,
        context.absenteeOptions,
        context.employeeTeamDepartments,
        context.permissionState,
      ),
    )
    .filter(
      (absence) =>
        !!absence.Employee &&
        checkAbsenteePermission(absence, context.approvableTeamDepartments, context.permissionState, 'approve'),
    );

export const filterIncomingExchanges = (
  exchanges: ExchangeWithScheduleModel[],
  userId: string | undefined,
): ExchangeWithScheduleModel[] => exchanges.filter((exchange) => isIncomingFor(exchange, userId));

export const filterOutgoingExchanges = (
  exchanges: ExchangeWithScheduleModel[],
  userId: string | undefined,
): ExchangeWithScheduleModel[] => exchanges.filter((exchange) => isOutgoingFor(exchange, userId));

export const toVisibleOpenShifts = (
  rows: PendingOpenShiftRow[],
  context: OpenShiftViewContext,
): PendingOpenShiftRow[] => {
  const enhance = enhanceOpenShift(
    context.teams,
    context.shifts,
    context.departments,
    context.locations,
    context.authenticatedUserId,
    context.employees,
  );

  return rows.filter((row) => row.instances_remaining > 0).map(enhance);
};

export const toOwnPendingAbsences = (rows: PendingAbsenceRow[], context: AbsenceViewContext): EnhancedAbsenceModel[] =>
  rows
    .map((row) =>
      enhanceAbsence(
        row,
        context.employees,
        context.absenteeOptions,
        context.employeeTeamDepartments,
        context.permissionState,
      ),
    )
    .filter((absence) => absence.status === AbsenceStatus.PENDING);

/**
 * The departments, per absence owner, where the signed-in user may approve that owner's absences.
 * Flexpool departments are already left out of `approvableTeamDepartments`.
 */
export const toApprovableDepartmentsByEmployee = (
  absences: EnhancedAbsenceModel[],
  context: Pick<AbsenceViewContext, 'approvableTeamDepartments' | 'permissionState'>,
): EmployeeTeamDepartments =>
  Object.fromEntries(
    absences.map((absence) => [
      absence.user_id,
      (context.approvableTeamDepartments[absence.user_id] ?? []).filter((departmentId) =>
        checkAbsenteePermission(absence, { [absence.user_id]: [departmentId] }, context.permissionState, 'approve'),
      ),
    ]),
  );
